import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import alqemiDataService, {
  Candidate,
  Offer,
} from "../../api/AlqemiDataService";

interface AlqemiFilter {
  field: string;
  value: any;
  operator?: string;
}

interface AlqemiArchiveDashboardContextType {
  offers: Offer[];
  candidates: Candidate[];
  currentFilters: AlqemiFilter[];
  loadingData: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  scanLimitReached: boolean;
  setCandidates: (candidates: Candidate[]) => void;
  addAlqemiFilter: (filter: AlqemiFilter) => void;
  removeAlqemiFilter: (field: string) => void;
  replaceAlqemiFilters: (filters: AlqemiFilter[]) => void;
  refreshData: () => void;
  loadMore: () => void;
}

const AlqemiArchiveDashboardContext =
  createContext<AlqemiArchiveDashboardContextType | null>(null);

export const useAlqemiArchiveDashboard = () => {
  const context = useContext(AlqemiArchiveDashboardContext);
  if (!context) {
    throw new Error(
      "useAlqemiArchiveDashboard must be used within AlqemiArchiveDashboardProvider"
    );
  }
  return context;
};

interface AlqemiArchiveDashboardProviderProps {
  children: React.ReactNode;
}

export default function AlqemiArchiveDashboardProvider({
  children,
}: AlqemiArchiveDashboardProviderProps) {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [currentFilters, setCurrentFilters] = useState<AlqemiFilter[]>(() => {
    const savedFilters = localStorage.getItem("alqemiArchiveFilters");
    if (!savedFilters) return [];
    // Exclude searchQuery from restored filters — the search input resets on refresh
    return JSON.parse(savedFilters).filter((f: AlqemiFilter) => f.field !== "searchQuery");
  });
  const [loadingData, setLoadingData] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [scanLimitReached, setScanLimitReached] = useState(false);
  const nextTokenRef = useRef<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoadingData(true);
    nextTokenRef.current = null;
    setHasMore(false);
    setScanLimitReached(false);

    try {
      const searchFilter = currentFilters.find(
        (f) => f.field === "searchQuery"
      );
      const candidateFilter = currentFilters.find(
        (f) => f.field === "currentCandidate"
      );
      const dateFilter = currentFilters.find(
        (f) => f.field === "dateRange"
      );

      // Always use paginated endpoint (works with and without candidate filter)
      const params: any = {
        pageSize: 100,
      };
      if (searchFilter) params.searchQuery = searchFilter.value;
      if (candidateFilter) params.currentCandidate = candidateFilter.value;
      if (dateFilter && dateFilter.value) {
        if (dateFilter.value.startDate) params.startDate = dateFilter.value.startDate;
        if (dateFilter.value.endDate) params.endDate = dateFilter.value.endDate;
      }

      const [paginatedResponse, candidatesData] = await Promise.all([
        alqemiDataService.getArchivedOffersPaginated(params),
        alqemiDataService.getCandidates(),
      ]);

      const filteredOffers = paginatedResponse.items.filter(
        (offer) => !offer.scraping_failed
      );

      setScanLimitReached(paginatedResponse.scanLimitReached || false);

      // Handle empty page with more data available (DynamoDB filter edge case)
      if (
        filteredOffers.length === 0 &&
        paginatedResponse.nextToken
      ) {
        nextTokenRef.current = paginatedResponse.nextToken;
        setHasMore(true);
        setOffers([]);
        setCandidates(candidatesData);
        // Auto-fetch next page
        setLoadingData(false);
        await loadMoreInternal(
          [], candidatesData, paginatedResponse.nextToken,
          searchFilter?.value, candidateFilter?.value,
          dateFilter?.value?.startDate, dateFilter?.value?.endDate
        );
        return;
      }

      nextTokenRef.current = paginatedResponse.nextToken;
      setHasMore(!!paginatedResponse.nextToken);
      setOffers(filteredOffers);
      setCandidates(candidatesData);
    } catch (error) {
      console.error("Error fetching archived dashboard data:", error);
      setOffers([]);
      setCandidates([]);
    } finally {
      setLoadingData(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilters]);

  const loadMoreInternal = async (
    currentOffers: Offer[],
    currentCandidates: Candidate[],
    token: string,
    searchQuery?: string,
    candidateId?: string,
    startDate?: string,
    endDate?: string,
  ) => {
    setLoadingMore(true);
    try {
      const params: any = { pageSize: 100, nextToken: token };
      if (searchQuery) params.searchQuery = searchQuery;
      if (candidateId) params.currentCandidate = candidateId;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await alqemiDataService.getArchivedOffersPaginated(params);

      const filteredItems = response.items.filter(
        (offer) => !offer.scraping_failed
      );

      nextTokenRef.current = response.nextToken;
      setHasMore(!!response.nextToken);

      if (response.scanLimitReached) {
        setScanLimitReached(true);
      }

      // If empty page but more pages exist, auto-fetch next
      if (filteredItems.length === 0 && response.nextToken) {
        await loadMoreInternal(currentOffers, currentCandidates, response.nextToken, searchQuery, candidateId, startDate, endDate);
        return;
      }

      setOffers([...currentOffers, ...filteredItems]);
    } catch (error) {
      console.error("Error loading more archived offers:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadMore = useCallback(() => {
    const token = nextTokenRef.current;
    if (!token || loadingMore) return;

    const searchFilter = currentFilters.find(
      (f) => f.field === "searchQuery"
    );
    const candidateFilter = currentFilters.find(
      (f) => f.field === "currentCandidate"
    );
    const dateFilter = currentFilters.find(
      (f) => f.field === "dateRange"
    );
    loadMoreInternal(
      offers, candidates, token,
      searchFilter?.value, candidateFilter?.value,
      dateFilter?.value?.startDate, dateFilter?.value?.endDate
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers, candidates, loadingMore, currentFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(
      "alqemiArchiveFilters",
      JSON.stringify(currentFilters)
    );
  }, [currentFilters]);

  const addAlqemiFilter = (filter: AlqemiFilter) => {
    setCurrentFilters((prev) => {
      const existing = prev.findIndex((f) => f.field === filter.field);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = filter;
        return updated;
      }
      return [...prev, filter];
    });
  };

  const removeAlqemiFilter = (field: string) => {
    setCurrentFilters((prev) => prev.filter((f) => f.field !== field));
  };

  const replaceAlqemiFilters = (filters: AlqemiFilter[]) => {
    setCurrentFilters(filters);
  };

  const refreshData = () => {
    fetchData();
  };

  const value: AlqemiArchiveDashboardContextType = {
    offers,
    candidates,
    currentFilters,
    loadingData,
    loadingMore,
    hasMore,
    scanLimitReached,
    setCandidates,
    addAlqemiFilter,
    removeAlqemiFilter,
    replaceAlqemiFilters,
    refreshData,
    loadMore,
  };

  return (
    <AlqemiArchiveDashboardContext.Provider value={value}>
      {children}
    </AlqemiArchiveDashboardContext.Provider>
  );
}
